import { useRef } from 'react';
import { IconFilterOff } from '@tabler/icons-react';

import { Button, ChipPicker, DatePicker, Text } from '@fragments';

import GranularityPopperContent from '../GranularityPopperContent';

import { DATE_INTERVAL_PRESETS, DATE_INTERVAL_PRESET_MAP, toDisplayDate } from '../../../enums/dateIntervalPresets';
import { CHARGE_STATUSES } from '../../../enums/chargeStatuses';
import { PAYMENT_METHODS } from '../../../enums/paymentMethods';
import { formatGranularity } from '../../../enums/granularityTypes';

import './styles.scss';

const STATUS_OPTIONS = CHARGE_STATUSES.map(s => ({ id: s.id, label: s.label }));
const METHOD_OPTIONS = PAYMENT_METHODS.map(m => ({ id: m.id, label: m.label }));
const PROVIDER_OPTIONS = [
    { id: 'asaas', label: 'Asaas' },
    { id: 'stripe', label: 'Stripe' },
];

export default function FiltersSection({
    dateRange,
    applyPreset,
    applyCustomRange,

    plans,
    planIds,
    setPlanIds,

    chargeStatuses,
    setChargeStatuses,

    chargeMethods,
    setChargeMethods,

    providerFilters,
    setProviderFilters,

    granularity,
    setGranularity,

    clearOptionalFilters,
}) {
    const intervalPickerRef = useRef(null);

    const hasOptionalFilters =
        planIds.length !== plans.length ||
        chargeStatuses.length > 0 ||
        chargeMethods.length > 0 ||
        providerFilters.length > 0;

    function handleDateChipClick(event) {
        intervalPickerRef.current?.open(event.currentTarget, {
            presetId: dateRange.presetId,
            from: dateRange.from,
            to: dateRange.to,
        });
    }

    function handleIntervalApply({ presetId, from, to }) {
        if (presetId && presetId !== 'custom') {
            applyPreset(presetId);
        } else {
            applyCustomRange(from, to);
        }
    }

    const dateLabel = dateRange.presetId === 'custom'
        ? `${toDisplayDate(dateRange.from)} — ${toDisplayDate(dateRange.to)}`
        : DATE_INTERVAL_PRESET_MAP[dateRange.presetId]?.label || 'Selecionar';

    const planOptions = plans.map(plan => ({
        id: plan.id,
        label: plan.label,
        searchLabel: plan.label,
        optionContent: (
            <span className="insights-plan-option">
                <Text.span contrast={plan.deleted_at ? 5 : 8}>
                    {plan.label}
                </Text.span>
                {plan.deleted_at && (
                    <span className="insights-plan-option__deleted-badge">
                        <Text.span contrast={5}>deletado</Text.span>
                    </span>
                )}
            </span>
        ),
    }));

    return (
        <div className="insights-filters">
            <Text.span contrast={5}>Filtrar por</Text.span>

            <ChipPicker
                title="Período"
                valueLabel={dateLabel}
                filled
                onClick={handleDateChipClick}
            />

            <DatePicker.Interval
                controlRef={intervalPickerRef}
                value={dateRange}
                presets={DATE_INTERVAL_PRESETS}
                maxDate={new Date()}
                onApply={handleIntervalApply}
            />

            <ChipPicker
                title="Planos"
                multiple
                activateOnHover
                value={planIds}
                options={planOptions}
                onChange={setPlanIds}
                searchable
            />

            <ChipPicker
                title="Status"
                multiple
                activateOnHover
                value={chargeStatuses}
                options={STATUS_OPTIONS}
                onChange={setChargeStatuses}
            />

            <ChipPicker
                title="Método"
                multiple
                activateOnHover
                value={chargeMethods}
                options={METHOD_OPTIONS}
                onChange={setChargeMethods}
            />

            <ChipPicker
                title="Provedor"
                multiple
                activateOnHover
                value={providerFilters}
                options={PROVIDER_OPTIONS}
                onChange={setProviderFilters}
            />

            <ChipPicker
                title="Granularidade"
                activateOnHover
                valueLabel={formatGranularity(granularity)}
                customContent={({ close }) => (
                    <GranularityPopperContent
                        value={granularity}
                        onApply={(next) => {
                            setGranularity(next);
                            close();
                        }}
                    />
                )}
            />

            {hasOptionalFilters && (
                <Button
                    color="overlay"
                    size="small"
                    startIcon={<IconFilterOff size={16} stroke={2} />}
                    text="Limpar filtros"
                    onClick={clearOptionalFilters}
                    preventLoading
                />
            )}
        </div>
    );
}
